import { useState } from 'react'
import {
  Button,
  ListSwitch,
  MasterSwitch,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@duck/ui'
import {
  ClipboardIcon,
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { trackAchievement, useTar } from '@/modules'

export interface ShareSettingsPatch {
  isPublic?: boolean
  showPercentile?: boolean
  showWorkTime?: boolean
  showMemberSince?: boolean
  showFeedbackExcerpt?: boolean
}

export interface VisibilityRailProps {
  showPercentile: boolean
  showWorkTime: boolean
  showMemberSince: boolean
  showFeedbackExcerpt: boolean
  percentileAvailable: boolean
  onPatch: (patch: ShareSettingsPatch) => void
}

export const VisibilityRail = (props: VisibilityRailProps) => {
  const { t } = useTar()
  const rows: Array<{
    key: string
    label: string
    checked: boolean
    disabled: boolean
    info?: string
    badge?: { label: string; tooltip: string }
    onChange: (v: boolean) => void
  }> = [
    {
      key: 'percentile',
      label: t('share.toggle.percentile'),
      checked: props.showPercentile && props.percentileAvailable,
      disabled: !props.percentileAvailable,
      info: t('share.toggle.percentileInfo'),
      badge: !props.percentileAvailable
        ? {
            label: t('share.toggle.percentileBadge'),
            tooltip: t('share.toggle.percentileBadgeTooltip'),
          }
        : undefined,
      onChange: (v: boolean) => props.onPatch({ showPercentile: v }),
    },
    {
      key: 'workTime',
      label: t('share.toggle.workTime'),
      checked: props.showWorkTime,
      disabled: false,
      onChange: (v: boolean) => props.onPatch({ showWorkTime: v }),
    },
    {
      key: 'memberSince',
      label: t('share.toggle.memberSince'),
      checked: props.showMemberSince,
      disabled: false,
      onChange: (v: boolean) => props.onPatch({ showMemberSince: v }),
    },
    {
      key: 'feedbackExcerpt',
      label: t('share.toggle.feedbackExcerpt'),
      checked: props.showFeedbackExcerpt,
      disabled: false,
      onChange: (v: boolean) => props.onPatch({ showFeedbackExcerpt: v }),
    },
  ]

  return (
    <aside className="bg-gradient-to-b from-card to-background border border-border/60 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5  text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
        <span>{t('share.toggle.sectionTitle')}</span>
      </div>

      <ul className="flex flex-col">
        {rows.map((row, idx) => (
          <li
            key={row.key}
            className={`flex items-center justify-between gap-3 py-3.5 ${
              idx > 0 ? 'border-t border-dashed border-border/50' : ''
            }`}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="flex flex-col gap-0.5 min-w-0">
                <div className="flex flex-row items-center gap-x-1.5 gap-y-1 min-w-0">
                  <span className="text-sm font-medium text-foreground">
                    {row.label}
                  </span>
                  {row.badge && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="inline-flex items-center  text-[8px] tracking-[0.18em] uppercase text-muted-foreground border border-border/60 bg-muted/30 rounded-full px-1.5 py-0.5 cursor-help shrink-0"
                            aria-label={row.badge.tooltip}
                          >
                            {row.badge.label}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-[240px] text-xs"
                        >
                          {row.badge.tooltip}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <span
                  className=" text-[9px] tracking-[0.18em] uppercase"
                  style={{
                    color: row.checked
                      ? 'hsl(var(--color-primary))'
                      : 'hsl(var(--color-muted-foreground))',
                  }}
                >
                  {row.checked
                    ? t('share.toggle.shown')
                    : t('share.toggle.hidden')}
                </span>
              </div>
              {row.info && !row.badge && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InformationCircleIcon
                        className="w-3.5 h-3.5 text-muted-foreground cursor-help shrink-0"
                        aria-label={row.info}
                      />
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="max-w-[240px] text-xs"
                    >
                      {row.info}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {!row.badge && (
              <ListSwitch
                checked={row.checked}
                onCheckedChange={row.onChange}
                disabled={row.disabled}
                aria-label={row.label}
                offLabel={t('share.toggle.off')}
                onLabel={t('share.toggle.on')}
              />
            )}
          </li>
        ))}
      </ul>
    </aside>
  )
}

export interface PublicLinkRailProps {
  publicUrl: string
  isPublic: boolean
  challengeSlug: string
  slug: string
  onPatch: (patch: ShareSettingsPatch) => void
}

export const PublicLinkRail = (props: PublicLinkRailProps) => {
  const { t } = useTar()
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(props.publicUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    trackAchievement('Achievement Share URL Copied', {
      challengeSlug: props.challengeSlug,
      slug: props.slug,
    })
  }

  return (
    <aside className="bg-gradient-to-b from-card to-background border border-border/60 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5  text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
        <span>{t('share.toggle.public')}</span>
      </div>

      <div
        className={`flex items-center justify-between gap-3 p-3.5 rounded-xl transition-colors ${
          props.isPublic
            ? 'bg-primary/10 border border-primary/30'
            : 'bg-muted/10 border border-border/60'
        }`}
      >
        <span className="font-fraunces italic text-[15px] text-foreground leading-none min-w-0">
          {props.isPublic ? t('share.toggle.live') : t('share.toggle.private')}
        </span>
        <MasterSwitch
          checked={props.isPublic}
          onCheckedChange={(v) => props.onPatch({ isPublic: v })}
          aria-label={t('share.toggle.public')}
        />
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug mt-2 mb-4">
        {t('share.toggle.publicHint')}
      </p>

      <div className="flex items-center gap-2 bg-background border border-dashed border-border rounded-lg px-3 py-2 mb-3">
        {/* Read-only input so the URL can be selected/copied manually. URLs are
            LTR data, so force direction regardless of UI locale. */}
        <input
          type="text"
          readOnly
          dir="ltr"
          value={props.publicUrl}
          onFocus={(e) => e.currentTarget.select()}
          onClick={(e) => e.currentTarget.select()}
          aria-label={t('share.toggle.public')}
          className="flex-1 min-w-0 bg-transparent font-mono text-[11px] text-foreground truncate outline-none cursor-pointer"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={copy}
          disabled={!props.isPublic}
          className=" text-[10px] tracking-[0.18em] uppercase"
        >
          {copied ? <CheckIcon className="w-3.5 h-3.5 mr-1.5" /> : <ClipboardIcon className="w-3.5 h-3.5 mr-1.5" />}
          {copied ? t('share.actions.copied') : t('share.actions.copy')}
        </Button>
        <Button
          size="sm"
          variant="default"
          asChild
          disabled={!props.isPublic}
          aria-disabled={!props.isPublic}
          className=" text-[10px] tracking-[0.18em] uppercase"
        >
          <a
            href={props.publicUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              trackAchievement('Achievement Public Page Opened', {
                challengeSlug: props.challengeSlug,
                slug: props.slug,
              })
            }
          >
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 mr-1.5" />
            {t('share.actions.viewPage')}
          </a>
        </Button>
      </div>
    </aside>
  )
}
